import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Container,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  ImageList,
  ImageListItem,
  IconButton,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  useTheme,
  Chip,
  Tooltip,
  Zoom,
  Fade,
  LinearProgress,
  FormHelperText,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  PhotoCamera as PhotoCameraIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  LocationOn as LocationOnIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useCreateLostItem, useFileUpload } from '../hooks/useApi';
import { useNotification } from '../contexts/NotificationContext';
import StoreSelectImproved from './StoreSelectImproved';

const steps = [
  {
    label: '基本情報の入力',
    description: 'アイテム名、発見場所、日時を入力してください',
    icon: <InfoIcon />
  },
  {
    label: '画像のアップロード',
    description: '落とし物の写真を追加してください（任意）',
    icon: <PhotoCameraIcon />
  },
  {
    label: '確認と登録',
    description: '入力内容を確認して登録してください',
    icon: <CheckCircleIcon />
  },
];

// 共通ロケーション候補
const commonLocations = [
  'レジ付近',
  '試着室',
  '入口・出口',
  '休憩スペース',
  'トイレ',
  '駐車場',
  '階段',
  'エレベーター',
  'エスカレーター',
  '店舗外',
  'その他',
];

// カテゴリー候補
const itemCategories = [
  '財布・小銭入れ',
  'バッグ・カバン',
  '鍵・キーホルダー',
  '携帯電話・スマートフォン',
  'イヤホン・ヘッドフォン',
  'アクセサリー',
  '衣類',
  '傘',
  '文房具',
  '本・雑誌',
  'その他',
];

const LostItemFormNew: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const { showSuccess, showError } = useNotification();

  const createItemMutation = useCreateLostItem();
  const fileUploadMutation = useFileUpload();

  // Form state
  const [activeStep, setActiveStep] = useState(0);
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [foundLocation, setFoundLocation] = useState<string>('');
  const [customLocation, setCustomLocation] = useState<string>('');
  const [foundAt, setFoundAt] = useState<string>(new Date().toISOString().slice(0, 16));
  const [status, setStatus] = useState<string>('Found');
  const [storeId, setStoreId] = useState<string>('');
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [category, setCategory] = useState<string>('');
  const [contactInfo, setContactInfo] = useState<string>('');

  // Validation states
  const [errors, setErrors] = useState<Record<string, string>>({});

  // バリデーション結果を計算する関数（状態を更新しない）
  const getValidationErrors = useCallback((step: number): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 0:
        if (!name.trim()) newErrors.name = 'アイテム名は必須です';
        if (!foundLocation.trim() && !customLocation.trim()) {
          newErrors.foundLocation = '発見場所は必須です';
        }
        if (!foundAt) newErrors.foundAt = '発見日時は必須です';
        if (!category) newErrors.category = 'カテゴリーを選択してください';
        break;
      case 1:
        // 画像は任意なのでエラーチェックなし
        break;
      case 2:
        // 最終確認なのでエラーチェックなし
        break;
    }

    return newErrors;
  }, [name, foundLocation, customLocation, foundAt, category]);

  // バリデーションを実行し、エラーを設定する関数
  const validateStep = useCallback((step: number): boolean => {
    const newErrors = getValidationErrors(step);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [getValidationErrors]);

  // エラーをクリアする関数をメモ化
  const clearError = useCallback((fieldName: string) => {
    setErrors(prev => ({ ...prev, [fieldName]: '' }));
  }, []);

  // フォームが有効かどうかをメモ化（単純化版）
  const isCurrentStepValid = useMemo(() => {
    switch (activeStep) {
      case 0:
        return !!(name.trim() && 
                 (foundLocation.trim() || customLocation.trim()) && 
                 foundAt && 
                 category);
      case 1:
        return true; // 画像は任意
      case 2:
        return true; // 最終確認
      default:
        return false;
    }
  }, [activeStep, name, foundLocation, customLocation, foundAt, category]);

  const getEffectiveFoundLocation = useCallback(() => {
    return foundLocation === 'その他' ? customLocation : foundLocation;
  }, [foundLocation, customLocation]);

  // 有効な発見場所をメモ化
  const effectiveFoundLocation = useMemo(() => {
    return foundLocation === 'その他' ? customLocation : foundLocation;
  }, [foundLocation, customLocation]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      
      // ファイルサイズとタイプのバリデーション
      const validFiles = files.filter(file => {
        if (file.size > 10 * 1024 * 1024) { // 10MB制限
          showError(`${file.name} のファイルサイズが大きすぎます（最大10MB）`);
          return false;
        }
        if (!file.type.startsWith('image/')) {
          showError(`${file.name} は画像ファイルではありません`);
          return false;
        }
        return true;
      });

      if (mediaFiles.length + validFiles.length > 5) {
        showError('画像は最大5枚まで追加できます');
        return;
      }

      setMediaFiles(prev => [...prev, ...validFiles]);

      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreviews(prev => [...prev, reader.result as string]);
        };
        reader.readAsDataURL(file);
      });
    }
  }, [mediaFiles.length, showError]);

  const handleRemoveImage = useCallback((indexToRemove: number) => {
    setMediaFiles(prev => prev.filter((_, index) => index !== indexToRemove));
    setImagePreviews(prev => prev.filter((_, index) => index !== indexToRemove));
  }, []);

  const handleNext = useCallback(() => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  }, [validateStep, activeStep]);

  const handleBack = useCallback(() => {
    setActiveStep(prev => prev - 1);
    setErrors({});
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!currentUser) {
      showError('ログインが必要です');
      return;
    }

    if (Object.keys(getValidationErrors(2)).length > 0) {
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(10);
      
      // ファイルアップロード
      let uploadedMediaUrls: string[] = [];
      if (mediaFiles.length > 0) {
        setUploadProgress(30);
        try {
          uploadedMediaUrls = await fileUploadMutation.mutateAsync(mediaFiles);
          setUploadProgress(70);
        } catch (error) {
          console.error('File upload error:', error);
          showError('ファイルアップロードに失敗しました');
          return;
        }
      }

      // 落とし物登録
      const newItem = {
        name: name.trim(),
        description: description.trim() || undefined,
        found_location: getEffectiveFoundLocation(),
        found_at: new Date(foundAt).toISOString(),
        status,
        media_urls: uploadedMediaUrls,
        store_id: storeId || 'default-store-id',
        category: category || undefined,
        contact_info: contactInfo.trim() || undefined,
      };

      setUploadProgress(90);
      try {
        await createItemMutation.mutateAsync(newItem);
        setUploadProgress(100);
        showSuccess('落とし物の登録が完了しました');
        navigate('/items');
      } catch (error) {
        console.error('Create item error:', error);
        showError('登録中にエラーが発生しました');
      }
    } catch (error) {
      console.error('Submit error:', error);
      showError('登録中にエラーが発生しました');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, [
    currentUser,
    getValidationErrors,
    mediaFiles,
    name,
    description,
    getEffectiveFoundLocation,
    foundAt,
    status,
    storeId,
    category,
    contactInfo,
    fileUploadMutation,
    createItemMutation,
    showError,
    showSuccess,
    navigate,
  ]);

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Fade in timeout={500}>
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 3 }}>
                <TextField
                  fullWidth
                  label="アイテム名"
                  variant="outlined"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (errors.name) clearError('name');
                  }}
                  required
                  error={!!errors.name}
                  helperText={errors.name || 'どんなアイテムかを入力してください'}
                  InputProps={{
                    startAdornment: <InfoIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />

                <FormControl fullWidth error={!!errors.category}>
                  <InputLabel>カテゴリー</InputLabel>
                  <Select
                    value={category}
                    onChange={(e) => {
                      setCategory(e.target.value);
                      if (errors.category) clearError('category');
                    }}
                    label="カテゴリー"
                    required
                  >
                    {itemCategories.map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {errors.category || 'アイテムのカテゴリーを選択してください'}
                  </FormHelperText>
                </FormControl>

                <TextField
                  fullWidth
                  label="説明"
                  variant="outlined"
                  multiline
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="色、サイズ、特徴などの詳細な説明を入力してください..."
                  helperText="落とし主が見つけやすくなるような特徴を記載してください"
                />

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <FormControl fullWidth error={!!errors.foundLocation}>
                    <InputLabel>発見場所</InputLabel>
                    <Select
                      value={foundLocation}
                      onChange={(e) => {
                        setFoundLocation(e.target.value);
                        if (errors.foundLocation) setErrors(prev => ({ ...prev, foundLocation: '' }));
                      }}
                      label="発見場所"
                      required
                      startAdornment={<LocationOnIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                    >
                      {commonLocations.map((location) => (
                        <MenuItem key={location} value={location}>
                          {location}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {errors.foundLocation || '落とし物を発見した場所を選択してください'}
                    </FormHelperText>
                  </FormControl>

                  {foundLocation === 'その他' && (
                    <TextField
                      fullWidth
                      label="詳細な場所"
                      variant="outlined"
                      value={customLocation}
                      onChange={(e) => {
                        setCustomLocation(e.target.value);
                        if (errors.foundLocation) setErrors(prev => ({ ...prev, foundLocation: '' }));
                      }}
                      required
                      error={!!errors.foundLocation}
                      helperText="具体的な場所を入力してください"
                    />
                  )}

                  <TextField
                    fullWidth
                    label="発見日時"
                    type="datetime-local"
                    variant="outlined"
                    value={foundAt}
                    onChange={(e) => {
                      setFoundAt(e.target.value);
                      if (errors.foundAt) clearError('foundAt');
                    }}
                    required
                    error={!!errors.foundAt}
                    helperText={errors.foundAt || '発見した日時を入力してください'}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: <ScheduleIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>ステータス</InputLabel>
                    <Select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      label="ステータス"
                    >
                      <MenuItem value="Found">未引き取り</MenuItem>
                                        <MenuItem value="Claimed">引き取り済み</MenuItem>
                                        <MenuItem value="Returned">返却完了</MenuItem>
                                        <MenuItem value="Disposed">処分済み</MenuItem>
                    </Select>
                  </FormControl>

                  {userProfile && userProfile.roles.includes('admin') && (
                    <StoreSelectImproved
                      value={storeId}
                      onChange={(newStoreId) => setStoreId(newStoreId)}
                      label="店舗"
                      placeholder="店舗を検索して選択してください"
                      allowManualInput={userProfile.roles.includes('admin')}
                      showMap={true}
                      helperText="管理者は店舗を選択するか、店舗IDを直接入力できます"
                    />
                  )}
                </Box>

                <TextField
                  fullWidth
                  label="連絡先情報（任意）"
                  variant="outlined"
                  value={contactInfo}
                  onChange={(e) => setContactInfo(e.target.value)}
                  placeholder="落とし主への連絡に必要な情報があれば入力してください"
                  helperText="内部メモとして使用されます"
                />
              </Box>
            </Box>
          </Fade>
        );

      case 1:
        return (
          <Fade in timeout={500}>
            <Box sx={{ mt: 2 }}>
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhotoCameraIcon color="primary" />
                <Typography variant="h6">
                  画像のアップロード
                </Typography>
                <Chip 
                  label={`${imagePreviews.length}/5`}
                  size="small" 
                  color={imagePreviews.length > 0 ? 'primary' : 'default'}
                />
              </Box>
              
              <Typography variant="body1" gutterBottom>
                落とし物の画像をアップロードしてください（任意、最大5枚まで）
              </Typography>
              
              <Paper
                sx={{
                  p: 4,
                  mb: 3,
                  border: `2px dashed ${theme.palette.divider}`,
                  backgroundColor: theme.palette.action.hover,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: theme.palette.action.selected,
                    borderColor: theme.palette.primary.main,
                  },
                }}
                component="label"
              >
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  disabled={mediaFiles.length >= 5}
                />
                <CloudUploadIcon sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  画像をドラッグ&ドロップまたはクリックして選択
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  JPG、PNG形式（最大10MB、5枚まで）
                </Typography>
                {mediaFiles.length >= 5 && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    最大5枚まで選択可能です
                  </Typography>
                )}
              </Paper>

              {imagePreviews.length > 0 && (
                <Zoom in timeout={300}>
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon color="success" />
                      プレビュー ({imagePreviews.length}枚)
                    </Typography>
                    <ImageList cols={3} rowHeight={164} gap={8}>
                      {imagePreviews.map((preview, index) => (
                        <ImageListItem key={index} sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden' }}>
                          <img
                            src={preview}
                            alt={`Preview ${index + 1}`}
                            loading="lazy"
                            style={{ borderRadius: 8, transition: 'transform 0.2s' }}
                          />
                          <Tooltip title="画像を削除">
                            <IconButton
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                color: 'error.main',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 1)',
                                  transform: 'scale(1.1)',
                                },
                              }}
                              onClick={() => handleRemoveImage(index)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </ImageListItem>
                      ))}
                    </ImageList>
                  </Box>
                </Zoom>
              )}
            </Box>
          </Fade>
        );

      case 2:
        return (
          <Fade in timeout={500}>
            <Box sx={{ mt: 2 }}>
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="success" />
                <Typography variant="h6">
                  登録内容の確認
                </Typography>
              </Box>
              
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 3 }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        アイテム名
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {name}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        カテゴリー
                      </Typography>
                      <Chip label={category} color="primary" variant="outlined" />
                    </Box>
                    
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        発見場所
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {effectiveFoundLocation}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        発見日時
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {new Date(foundAt).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      ステータス
                      </Typography>
                      {(() => {
                      const labels: Record<string, string> = {
                        Found: '未引き取り',
                        Claimed: '引き取り済み',
                        Returned: '返却完了',
                        Disposed: '処分済み',
                      };
                      const colors: Record<string, 'default'|'primary'|'secondary'|'error'|'info'|'success'|'warning'> = {
                        Found: 'warning',
                        Claimed: 'info',
                        Returned: 'success',
                        Disposed: 'default',
                      };
                      const label = labels[status] || status;
                      const color = colors[status] || 'default';
                      return <Chip label={label} color={color} variant="outlined" />;
                      })()}
                    </Box>
                    
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        画像
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {imagePreviews.length}枚
                      </Typography>
                    </Box>
                    
                    {description && (
                      <Box sx={{ gridColumn: '1 / -1' }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          説明
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {description}
                        </Typography>
                      </Box>
                    )}
                    
                    {contactInfo && (
                      <Box sx={{ gridColumn: '1 / -1' }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          連絡先情報
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {contactInfo}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>

              {uploading && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    アップロード中... {uploadProgress}%
                  </Typography>
                  <LinearProgress variant="determinate" value={uploadProgress} />
                </Box>
              )}
            </Box>
          </Fade>
        );

      default:
        return null;
    }
  };

  if (!currentUser) {
    return (
      <Container maxWidth="lg">
        <Alert severity="warning">
          落とし物を登録するにはログインが必要です。
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
          新規落とし物登録
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          ステップに従って落とし物の情報を登録してください
        </Typography>
        
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    icon={
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          backgroundColor: index <= activeStep ? 'primary.main' : 'grey.300',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.3s ease',
                        }}
                      >
                        {index < activeStep ? <CheckCircleIcon /> : step.icon}
                      </Box>
                    }
                  >
                    <Box>
                      <Typography variant="h6" component="span">
                        {step.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {step.description}
                      </Typography>
                    </Box>
                  </StepLabel>
                  <StepContent>
                    {renderStepContent(index)}
                    <Box sx={{ mt: 3, mb: 2 }}>
                      <Button
                        variant="contained"
                        onClick={index === steps.length - 1 ? handleSubmit : handleNext}
                        disabled={!isCurrentStepValid || uploading}
                        startIcon={
                          index === steps.length - 1 ? (
                            uploading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />
                          ) : null
                        }
                        sx={{ mr: 1, minWidth: 120 }}
                        size="large"
                      >
                        {index === steps.length - 1 ? (uploading ? '登録中...' : '登録') : '次へ'}
                      </Button>
                      <Button
                        disabled={index === 0 || uploading}
                        onClick={handleBack}
                        sx={{ mr: 1 }}
                        size="large"
                      >
                        戻る
                      </Button>
                      <Button
                        onClick={() => navigate('/items')}
                        startIcon={<CancelIcon />}
                        disabled={uploading}
                        color="inherit"
                        size="large"
                      >
                        キャンセル
                      </Button>
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>

            {activeStep === steps.length && (
              <Fade in timeout={500}>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                  <Typography variant="h5" gutterBottom>
                    登録が完了しました！
                  </Typography>
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    落とし物の登録が正常に完了しました。
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/items')}
                    sx={{ mt: 2 }}
                    size="large"
                  >
                    一覧に戻る
                  </Button>
                </Box>
              </Fade>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default LostItemFormNew;
